import {useEffect, useReducer, useRef} from "react";
import {initialState, reducer} from "./state";
import {ActionTypeReducer} from "helpers/types";
import axios from "axios";
import {validatorEngine, convertMasksFormat} from "../../../../../helpers/functions";
import {UsuarioModel} from "models/user_model";
import {EmpresaModelGet, EmpresaModelInsert} from "models/empresa";

type changeInputType = ActionTypeReducer<typeof initialState.company>
type changeInputTypeContact = ActionTypeReducer<typeof initialState.company.ContatoEmpresa[0]>
type changeInputTypeAddress = ActionTypeReducer<typeof initialState.company.EnderecoEmpresa[0]>

export const useNewCompany = () => {

    const [state, dispatch] = useReducer(reducer, initialState);

    const cancelRef = useRef();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        getLoggedUser();
        dispatch({type: "setLoading", payload: true});

        try {
            const companies = await loadCompanies();

            dispatch({type: "setCompanyList", payload: companies.data});
        } catch (e) {
            console.error("useEffect.loadCompany ", e);
            showError("Erro", "Erro ao carregar dados")
        } finally {
            dispatch({type: "setLoading", payload: false});
        }
    }

    const getLoggedUser = () => {
        const userData = JSON.parse(localStorage.getItem("user"));
        dispatch({type: "setLoggedUser", payload: userData});

        return userData as UsuarioModel;
    }

    const loadCompanies = async () => {
        return axios.get<EmpresaModelGet[]>(
            process.env.REACT_APP_API_URL + "/empresa"
        );
    }

    const validateCompanyFields = () => {
        const {company} = state;
        const validations: validatorEngine.type<typeof initialState.companyFormFieldsError> = {
            razaoSocial: {
                test: () => validatorEngine.testString('minLength', company.razaoSocial, 8),
                errorMessage: "O Razão social precisa ter mais de 8 caracteres"
            },
            nomeFantasia: {
                test: () => validatorEngine.testString('minLength', company.nomeFantasia, 8),
                errorMessage: "O Nome fantasia precisa ter mais de 8 caracteres",
            },
            inscricaoMunicipal: {
                test: () => validatorEngine.testString('required', company.inscricaoMunicipal),
                errorMessage: "Inscrição municipal é requerida"
            },
            inscricaoEstadual: {
                test: () => validatorEngine.testString('required', company.inscricaoEstadual),
                errorMessage: "Inscrição estadual é requerida"
            },
            cnpj: {
                test: () => validatorEngine.testString('cnpj', company.cnpj),
                errorMessage: "CNPJ inválido"

            },
            cnae: {
                test: () => validatorEngine.testString('required', company.cnae),
                errorMessage: "CNAE é requerido"
            },
            atividadePrincipal: {
                test: () => validatorEngine.testString('required', company.atividadePrincipal),
                errorMessage: "Atividade principal é requerida"
            },
            regimeTributario: {
                test: () => validatorEngine.testString('required', company.regimeTributario),
                errorMessage: "Regime tributário é requerido"
            },
            tamanhoEmpresa: {
                test: () => validatorEngine.testString('required', company.tamanhoEmpresa),
                errorMessage: "Tamanho da empresa é requerido"
            },
            faturamentoUltimoAno: {
                test: () => validatorEngine.testString('required', company.faturamentoUltimoAno),
                errorMessage: "Faturamento do último ano é requerido"
            },
            quantidadeFuncionarios: {
                test: () => validatorEngine.testString('required', company.quantidadeFuncionarios),
                errorMessage: "Quantidade de funcionários é requerida"
            },
            "EnderecoEmpresa.cep": {
                test: () => validatorEngine.testString('cep', company.EnderecoEmpresa[0].cep),
                errorMessage: "CEP inválido"
            },
            "EnderecoEmpresa.estado": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].estado),
                errorMessage: "Estado é requerido"
            },
            "EnderecoEmpresa.cidade": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].cidade),
                errorMessage: "Cidade é requerida"
            },
            "EnderecoEmpresa.bairro": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].bairro),
                errorMessage: "Bairro é requerido"
            },
            "EnderecoEmpresa.local": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].local),
                errorMessage: "Local é requerido"
            },
            "EnderecoEmpresa.numero": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].numero),
                errorMessage: "Número é requerido"
            },
            "EnderecoEmpresa.complemento": {
                test: () => validatorEngine.testString('required', company.EnderecoEmpresa[0].complemento),
                errorMessage: "Complemento é requerido"
            },
            "ContatoEmpresa.nome": {
                test: () => validatorEngine.testString('required', company.ContatoEmpresa[0].nome),
                errorMessage: "Nome é requerido"
            },
            "ContatoEmpresa.telefone": {
                test: () => validatorEngine.testString('phone', company.ContatoEmpresa[0].telefone),
                errorMessage: "Telefone inválido"
            },
            "ContatoEmpresa.email": {
                test: () => validatorEngine.testString('email', company.ContatoEmpresa[0].email),
                errorMessage: "Email inválido"
            },
        };

        const {isValid, errors} = validatorEngine.build(validations);

        dispatch({
            type: "setCompanyFormFieldsError",
            payload: {...state.companyFormFieldsError, ...errors}
        });

        return isValid;
    };

    const displayInputError = (type: keyof typeof initialState.companyFormFieldsError) => {
        const error = state.companyFormFieldsError[type];

        return ({
            error: !!error,
            errorMessage: error
        })
    }

    const addRegister = () => {
        const {id, EnderecoEmpresa, ContatoEmpresa, ...payload} = state.company;

        const newCompany: EmpresaModelInsert = {
            ...payload,
            cnpj: convertMasksFormat("cnpj", payload.cnpj),
            quantidadeFuncionarios: parseInt(payload.quantidadeFuncionarios),
            faturamentoUltimoAno: parseInt(payload.faturamentoUltimoAno),
            contato: [
                {
                    cargo: '',
                    celular: '',
                    email: ContatoEmpresa[0].email,
                    nome: ContatoEmpresa[0].nome,
                    telefone: convertMasksFormat("phone", ContatoEmpresa[0].telefone),
                    principal: true
                }
            ],
            endereco: [
                {
                    cep: convertMasksFormat("cep", EnderecoEmpresa[0].cep),
                    local: EnderecoEmpresa[0].local,
                    numero: EnderecoEmpresa[0].numero,
                    bairro: EnderecoEmpresa[0].bairro,
                    cidade: EnderecoEmpresa[0].cidade,
                    estado: EnderecoEmpresa[0].estado,
                    complemento: EnderecoEmpresa[0].complemento,
                    principal: true
                }
            ]
        }

        console.log("addRegister", newCompany);

        return axios.post(
            process.env.REACT_APP_API_URL + "/empresa/",
            newCompany
        );
    }

    const deleteRegister = (company: EmpresaModelGet) => {
        return axios.delete(
            process.env.REACT_APP_API_URL + "/empresa/" + company.id
        );
    }

    const patchRegister = () => {
        const {EnderecoEmpresa, ContatoEmpresa, ...payload} = state.company;

        const updateCompany: EmpresaModelInsert = {
            ...payload,
            cnpj: convertMasksFormat("cnpj", payload.cnpj),
            quantidadeFuncionarios: parseInt(payload.quantidadeFuncionarios),
            faturamentoUltimoAno: parseInt(payload.faturamentoUltimoAno),
            contato: [
                {
                    cargo: '',
                    celular: '',
                    email: ContatoEmpresa[0].email,
                    nome: ContatoEmpresa[0].nome,
                    telefone: convertMasksFormat("phone", ContatoEmpresa[0].telefone),
                    principal: true
                }
            ],
            endereco: [
                {
                    cep: convertMasksFormat("cep", EnderecoEmpresa[0].cep),
                    local: EnderecoEmpresa[0].local,
                    numero: EnderecoEmpresa[0].numero,
                    bairro: EnderecoEmpresa[0].bairro,
                    cidade: EnderecoEmpresa[0].cidade,
                    estado: EnderecoEmpresa[0].estado,
                    complemento: EnderecoEmpresa[0].complemento,
                    principal: true
                }
            ]
        }

        console.log("patchRegister", updateCompany);

        return axios.patch(
            process.env.REACT_APP_API_URL + "/empresa/" + payload.id,
            updateCompany
        );
    }

    const saveCompany = async () => {
        if (!validateCompanyFields()) return;

        dispatch({type: "setLoading", payload: true});

        try {
            if (state.company.id) {
                await patchRegister();
            } else {
                await addRegister();
            }

            showModal(false);
            await loadData();
        } catch (e) {
            console.error("useNewCompany.saveCompany ", e);
            showError("Erro", "Erro ao salvar empresa")
        } finally {
            dispatch({type: "setLoading", payload: false});
        }
    }

    const deleteCompany = async (company: EmpresaModelGet) => {
        dispatch({type: "setLoading", payload: true});

        try {
            await deleteRegister(company);

            const list = state.companyList.filter((item) => item.id !== company.id);
            dispatch({type: "setCompanyList", payload: list});
        } catch (e) {
            console.error("useNewCompany.deleteCompany ", e);
            showError("Erro", "Erro ao deletar empresa")
        } finally {
            dispatch({type: "setLoading", payload: false});
        }
    }

    const editCompany = (company: EmpresaModelGet) => {
        const companyData = mergeComapanyData(initialState.company, company);

        dispatch({type: "setCompany", payload: companyData});
        showModal(true);
    }

    const add = () => {
        showModal(true);
    };

    const mergeComapanyData = (defaultData: typeof state.company, apiData: Partial<EmpresaModelGet>) => {
        return {
            ...defaultData,
            ...apiData,
            ContatoEmpresa: apiData.ContatoEmpresa && apiData.ContatoEmpresa.length > 0 ? apiData.ContatoEmpresa : defaultData.ContatoEmpresa,
            EnderecoEmpresa: apiData.EnderecoEmpresa && apiData.EnderecoEmpresa.length > 0 ? apiData.EnderecoEmpresa : defaultData.EnderecoEmpresa,
        };
    };

    const changeInput = ({type, payload}: changeInputType) => {
        const company = {...state.company, [type]: payload};
        dispatch({type: "setCompany", payload: company});
    }

    const changeContactInput = ({type, payload}: changeInputTypeContact) => {
        const contact = {...state.company.ContatoEmpresa[0], [type]: payload};

        dispatch({type: "setCompany", payload: {...state.company, ContatoEmpresa: [contact]}});
    }

    const changeAddressInput = ({type, payload}: changeInputTypeAddress) => {
        const address = {...state.company.EnderecoEmpresa[0], [type]: payload};

        dispatch({type: "setCompany", payload: {...state.company, EnderecoEmpresa: [address]}});
    }


    const showModal = (show: boolean) => {
        dispatch({type: "setShow", payload: show});
        dispatch({type: "setError", payload: false});
        if (!show) {
            dispatch({type: "setCompany", payload: initialState.company});
            dispatch({type: "setCompanyFormFieldsError", payload: initialState.companyFormFieldsError});
        }
    };

    const closeDelete = () => {
        dispatch({type: "setShowDelete", payload: false});
    };

    const closeError = () => {
        dispatch({type: "setError", payload: false});
    };

    const showError = (title: string, msg: string) => {
        dispatch({type: "setError", payload: true});
        dispatch({type: "setErrorTitle", payload: title});
        dispatch({type: "setErrorMsg", payload: msg});
    };

    return {
        state,
        cancelRef,
        add,
        closeDelete,
        closeError,
        showError,
        showModal,
        changeInput,
        changeContactInput,
        changeAddressInput,
        saveCompany,
        deleteCompany,
        editCompany,
        displayInputError
    };
};

export namespace useNewCompany {
    export type type = ReturnType<typeof useNewCompany>;
}
